import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@nexus/ui";
import type { ChangelogEntry } from "@/features/changelog/api/changelog.api";
import {
  formatDeployedAt,
  getEntryState,
  type ChangelogEntryState,
} from "@/features/changelog/lib/changelog-display";
import {
  buildRoundupCoverage,
  buildTimeline,
  expandTimelineDays,
  startOfDay,
  toDayKey,
  type DateWindow,
} from "@/features/changelog/lib/changelog-templates";

/** Beyond this a busy day would out-grow the chart; the label carries the true count. */
const MAX_BLOCKS_PER_DAY = 6;

/** Past this many columns, a label under every day turns the axis into noise. */
const DENSE_AXIS_THRESHOLD = 21;
const DENSE_LABEL_EVERY = 7;

/**
 * Mount choreography: the card settles, then a left-to-right wave of columns,
 * each growing its blocks from the bottom. Caps keep a 30-day axis from turning
 * the tail into a wait. Durations stay short — design context is 150ms motion,
 * and a chart entrance is allowed a little more without feeling laggy.
 */
const COLUMN_STAGGER_MS = 20;
const COLUMN_STAGGER_CAP_MS = 360;
const BLOCK_STAGGER_MS = 28;
const BLOCK_STAGGER_CAP_MS = 120;
const COVERAGE_DELAY_MS = COLUMN_STAGGER_CAP_MS + 80;

const COLUMN_ENTER =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none";
const BLOCK_ENTER =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:fill-mode-both motion-safe:duration-200 motion-safe:ease-out motion-reduce:animate-none";
const COVERAGE_ENTER =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none";
const SECTION_ENTER =
  "motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none";

/**
 * Shipped, approved and failed match the row badges, so a column and its rows
 * say the same thing. The three the badge draws as one neutral — draft,
 * generating, pending — are split apart here: a badge also spells its state out
 * in words, whereas a block has nothing but its fill to say it with.
 *
 * Two lanes: blue is the machine working, and nothing in it is anyone's cue to
 * act. Grey through amber to green is the human pipeline, and it is deliberately
 * the heavier of the two.
 */
const STATE_FILL: Record<ChangelogEntryState, string> = {
  sent: "bg-success",
  sending: "bg-info",
  approved: "bg-warning",
  failed: "bg-error",
  // Started and never came back. Framed rather than filled, so it reads as
  // unfinished beside an outright failure instead of duplicating it.
  stalled: "bg-error/40",
  // The review queue is what this page exists for, so it carries full weight.
  // Ink rather than muted-foreground: at 6–14px a mid-grey wash reads as
  // empty chart, and this is the state that should pull the eye first.
  draft: "bg-foreground/75",
  // Blue is the machine lane: nothing here is waiting on a person. The three
  // blues are told apart by how full the block is rather than by shade —
  // empty, part, solid. Shade alone put generating and sending 1.4:1 apart,
  // which is not a difference anyone reads at this size.
  generating: "bg-info/55",
  pending: "bg-transparent",
};

/** Frame around a deploy that a roundup has already taken. Half-opacity on
 *  muted-foreground disappeared into the card at this size. */
const ROUNDUP_FRAME = "border border-foreground/40 bg-clip-content p-px";

/** Ring on a block whose row is ticked — distinct from the roundup frame. */
const PICKED_RING =
  "outline outline-2 outline-offset-1 outline-primary";

/**
 * Outlines carry the states whose fill is too faint to hold an edge on its own.
 * The border is a solid token, so the block stays legible however light the
 * wash inside it is.
 */
const STATE_OUTLINE: Partial<Record<ChangelogEntryState, string>> = {
  stalled: "border border-error",
  generating: "border border-info",
  pending: "border border-info",
};

/**
 * Legend reading order, pipeline-first. Only states actually on the chart get
 * a key — "0 Draft · 0 Approved · 0 Sent" over a wall of generating blocks
 * explains nothing.
 */
const LEGEND_ORDER: ChangelogEntryState[] = [
  "generating",
  "pending",
  "draft",
  "approved",
  "sending",
  "sent",
  "failed",
  "stalled",
];

/**
 * Blocks grow to fill the chart when days are quiet, so a two-deploy week
 * reads as bars rather than crumbs at the bottom of an empty box. Keyed by the
 * busiest visible day; rolled-up blocks are 2px taller to fit their frame.
 * Every combination fills the same 56px chart within a few pixels.
 */
const BLOCK_HEIGHT: Record<number, { block: string; roundup: string }> = {
  1: { block: "h-3.5", roundup: "h-4" },
  2: { block: "h-3.5", roundup: "h-4" },
  3: { block: "h-3.5", roundup: "h-4" },
  4: { block: "h-2.5", roundup: "h-3" },
  5: { block: "h-2", roundup: "h-2.5" },
  6: { block: "h-1.5", roundup: "h-2" },
};

interface ChangelogDeployActivityProps {
  entries: ChangelogEntry[];
  /**
   * Stretch being filtered on, or null for every deploy loaded. Drawn in full
   * even where it is quiet: a sprint with two deploys in it should look like a
   * quiet sprint, not like a two-day one.
   */
  window?: DateWindow | null;
  /** Local calendar date being filtered on, or null for every day. */
  selectedDay: string | null;
  onSelectDay: (day: string | null) => void;
  /**
   * Roundup title per version tag, for deploys already rolled up. Null when
   * roundups are unreadable, which hides the coverage rail rather than
   * reporting everything as un-announced.
   */
  roundupTitleByVersion?: ReadonlyMap<string, string> | null;
  /** Version tags currently ticked in the table, so the chart marks the same set. */
  pickedVersions?: ReadonlySet<string>;
}

/**
 * How often production is shipped, and how much of it nobody has signed off
 * yet. The table answers both one row at a time; a fortnight of quiet followed
 * by six deploys in a day is only visible from above. Each block is one
 * deploy — hovering names its version, state, and the roundup that took it.
 */
export function ChangelogDeployActivity({
  entries,
  window = null,
  selectedDay,
  onSelectDay,
  roundupTitleByVersion = null,
  pickedVersions,
}: ChangelogDeployActivityProps) {
  const { t, i18n } = useTranslation();

  const days = useMemo(() => {
    const timeline = buildTimeline(entries, window);
    return timeline ? expandTimelineDays(timeline) : [];
  }, [entries, window]);

  const coverage = useMemo(
    () => buildRoundupCoverage(days, roundupTitleByVersion),
    [days, roundupTitleByVersion],
  );

  const stateCounts = useMemo(() => {
    const counts = new Map<ChangelogEntryState, number>();
    for (const entry of entries) {
      const state = getEntryState(entry);
      counts.set(state, (counts.get(state) ?? 0) + 1);
    }
    return counts;
  }, [entries]);

  if (days.length === 0) return null;

  const todayKey = toDayKey(startOfDay(new Date()));
  const hasPicks = entries.some((entry) => pickedVersions?.has(entry.versionTag) === true);
  const labelEvery = days.length > DENSE_AXIS_THRESHOLD ? DENSE_LABEL_EVERY : 1;
  const hasRoundups =
    roundupTitleByVersion != null &&
    entries.some((entry) => roundupTitleByVersion.has(entry.versionTag));
  const legendStates = LEGEND_ORDER.filter(
    (state) => (stateCounts.get(state) ?? 0) > 0,
  );
  const maxStack = Math.min(
    MAX_BLOCKS_PER_DAY,
    Math.max(1, ...days.map((day) => day.entries.length)),
  );
  const blockHeight = BLOCK_HEIGHT[maxStack]!;
  const selectedDate = days.find((day) => day.key === selectedDay)?.date ?? null;

  const dayLabel = (date: Date) =>
    date.toLocaleDateString(i18n.language, { day: "numeric", month: "short" });
  const weekdayLabel = (date: Date) =>
    date.toLocaleDateString(i18n.language, { weekday: "narrow" });
  const stateLabel = (state: ChangelogEntryState) =>
    t(`admin.changelog.state.${state}`, { defaultValue: state });

  // Remounting the chart when the stretch changes restarts the wave, so a
  // filter click reads as a new view arriving rather than columns reshuffling.
  const mountKey =
    window === null
      ? "all"
      : `${toDayKey(window.start)}-${toDayKey(window.end)}`;

  return (
    <section
      className={cn(
        "flex shrink-0 flex-col gap-2 rounded-md border border-border-subtle bg-card px-3 py-2.5",
        SECTION_ENTER,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-foreground/70">
            {t("admin.changelog.activity.label", "Ship activity")}
          </span>
          {selectedDate ? (
            <Button
              variant="ghost"
              size="sm"
              iconStart="x"
              className="h-6 rounded-sm text-[11px]"
              onClick={() => onSelectDay(null)}
            >
              {t("admin.changelog.activity.clearDay", "Showing {{date}}", {
                date: dayLabel(selectedDate),
              })}
            </Button>
          ) : null}
        </div>
        <ul className="m-0 flex list-none items-center gap-3 p-0">
          {legendStates.map((state) => {
            const count = stateCounts.get(state) ?? 0;
            return (
              <li
                key={state}
                className="flex items-center gap-1.5 text-[10px] tabular-nums text-foreground/70"
              >
                <span
                  aria-hidden
                  className={cn(
                    "size-2.5 rounded-[2px]",
                    STATE_FILL[state],
                    STATE_OUTLINE[state],
                  )}
                />
                {t("admin.changelog.activity.legendItem", "{{count}} {{state}}", {
                  count,
                  state: stateLabel(state),
                })}
              </li>
            );
          })}
          {hasRoundups ? (
            <li className="flex items-center gap-1.5 text-[10px] text-foreground/70">
              <span
                aria-hidden
                className={cn("size-2.5 rounded-[2px] bg-foreground/75", ROUNDUP_FRAME)}
              />
              {t("admin.changelog.activity.legendRoundup", "In roundup")}
            </li>
          ) : null}
        </ul>
      </div>

      <TooltipProvider delayDuration={150}>
        <div
          key={mountKey}
          className="flex items-stretch gap-[3px]"
          role="group"
          aria-label={t("admin.changelog.activity.label", "Ship activity")}
        >
          {days.map((day, index) => {
            const isSelected = day.key === selectedDay;
            const isQuiet = day.entries.length === 0;
            const isToday = day.key === todayKey;
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
            const showLabel =
              labelEvery === 1 || index % labelEvery === 0 || day.date.getDate() === 1;
            const label = t("admin.changelog.activity.day", {
              date: dayLabel(day.date),
              count: day.entries.length,
            });
            // Stacked bottom-up in deploy order, so a column reads as the day did.
            const ordered = [...day.entries].sort(
              (a, b) =>
                new Date(a.deployedAt).getTime() - new Date(b.deployedAt).getTime(),
            );
            const columnDelay = Math.min(index * COLUMN_STAGGER_MS, COLUMN_STAGGER_CAP_MS);

            return (
              <div
                key={day.key}
                className={cn("flex min-w-0 flex-1 flex-col gap-1", COLUMN_ENTER)}
                style={{ animationDelay: `${columnDelay}ms` }}
              >
                <button
                  type="button"
                  aria-pressed={isSelected}
                  disabled={isQuiet}
                  className={cn(
                    "flex h-14 cursor-pointer flex-col-reverse items-center justify-start gap-0.5 rounded-sm px-0.5 pt-1 transition-[background-color,opacity] duration-150 ease-out motion-reduce:transition-none",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring",
                    // muted ≈ card, so tint from foreground instead of muted.
                    isWeekend && !isSelected && "bg-foreground/[0.04]",
                    isQuiet && "cursor-default",
                    isSelected && "bg-foreground/10",
                    !isSelected && !isQuiet && "hover:bg-foreground/8",
                    selectedDay && !isSelected && "opacity-40",
                  )}
                  onClick={() => onSelectDay(isSelected ? null : day.key)}
                >
                  <span className="sr-only">
                    {label}
                    {isToday
                      ? ` · ${t("admin.changelog.activity.today", "Today")}`
                      : null}
                  </span>
                  {isQuiet ? (
                    <span
                      aria-hidden
                      className="h-0.5 w-full max-w-10 rounded-[1px] bg-foreground/25"
                    />
                  ) : (
                    ordered.slice(0, MAX_BLOCKS_PER_DAY).map((entry, blockIndex) => {
                      const state = getEntryState(entry);
                      const roundupTitle =
                        roundupTitleByVersion?.get(entry.versionTag) ?? null;
                      const isPicked = pickedVersions?.has(entry.versionTag) === true;
                      const isBusy = state === "sending" || state === "generating";
                      // Earliest deploy is at the bottom of the stack, so it
                      // lands first and later ones stack on top of it.
                      const blockDelay =
                        columnDelay +
                        Math.min(blockIndex * BLOCK_STAGGER_MS, BLOCK_STAGGER_CAP_MS);

                      return (
                        <Tooltip key={entry.versionTag}>
                          <TooltipTrigger asChild>
                            <span
                              data-picked={isPicked ? "true" : undefined}
                              className={cn(
                                "w-full max-w-10 origin-bottom rounded-[2px] transition-transform duration-150 ease-out hover:scale-110 motion-reduce:transition-none motion-reduce:hover:scale-100",
                                BLOCK_ENTER,
                                blockHeight.block,
                                STATE_FILL[state],
                                STATE_OUTLINE[state],
                                isBusy && "animate-pulse motion-reduce:animate-none",
                                // A rolled-up deploy is drawn boxed — the frame
                                // says "packaged", the fill still says state.
                                roundupTitle !== null &&
                                  cn(blockHeight.roundup, ROUNDUP_FRAME),
                                hasPicks && !isPicked && "opacity-35",
                                isPicked && PICKED_RING,
                              )}
                              style={{ animationDelay: `${blockDelay}ms` }}
                            />
                          </TooltipTrigger>
                          {/* Beside the column, not above it — a tooltip on
                              top would cover the very blocks the cursor is
                              climbing towards. Radix flips it left at the
                              right edge. */}
                          <TooltipContent
                            side="right"
                            align="start"
                            sideOffset={8}
                            className="flex flex-col gap-1"
                          >
                            <p className="m-0 flex items-center gap-2 font-medium">
                              <span className="font-mono">{entry.versionTag}</span>
                              <span className="flex items-center gap-1 font-normal">
                                <span
                                  aria-hidden
                                  className={cn(
                                    "size-1.5 rounded-full",
                                    STATE_FILL[state],
                                    STATE_OUTLINE[state],
                                  )}
                                />
                                {stateLabel(state)}
                              </span>
                            </p>
                            {entry.headline ? (
                              <p className="m-0 line-clamp-2 opacity-80">
                                {entry.headline}
                              </p>
                            ) : null}
                            <p className="m-0 opacity-80">
                              {t("admin.changelog.deployedAt", "released {{at}}", {
                                at: formatDeployedAt(entry.deployedAt, i18n.language),
                              })}
                            </p>
                            {roundupTitle !== null ? (
                              <p className="m-0 flex items-center gap-1">
                                <Icon
                                  name="package"
                                  size="xs"
                                  color="inherit"
                                  aria-hidden
                                />
                                {t(
                                  "admin.changelog.activity.inRoundup",
                                  "In roundup: {{title}}",
                                  { title: roundupTitle },
                                )}
                              </p>
                            ) : null}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })
                  )}
                </button>
                <div
                  aria-hidden
                  className="flex min-h-[22px] flex-col items-center gap-px leading-none"
                >
                  {showLabel ? (
                    <>
                      <span
                        className={cn(
                          "text-[9px] uppercase",
                          isToday
                            ? "font-semibold text-primary"
                            : "text-foreground/65",
                        )}
                      >
                        {weekdayLabel(day.date)}
                      </span>
                      <span
                        className={cn(
                          "truncate text-[9px] tabular-nums",
                          isToday
                            ? "font-semibold text-primary"
                            : "text-foreground/55",
                        )}
                      >
                        {day.date.getDate() === 1 || index === 0
                          ? dayLabel(day.date)
                          : day.date.getDate()}
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {coverage.length > 0 ? (
          <ul
            className={cn("m-0 grid list-none gap-[3px] p-0", COVERAGE_ENTER)}
            style={{
              gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`,
              animationDelay: `${COVERAGE_DELAY_MS}ms`,
            }}
            aria-label={t(
              "admin.changelog.activity.coverage",
              "Roundup coverage",
            )}
          >
            {coverage.map((segment) => {
              const label =
                segment.title ??
                t("admin.changelog.activity.notInRoundup", {
                  count: segment.count,
                });

              return (
                <li
                  key={segment.title ?? "pending"}
                  // Spanning grid tracks rather than repeating a cell per day
                  // makes the run swallow the gaps between its columns, so one
                  // roundup reads as one rail instead of a dashed line.
                  style={{
                    gridColumn: `${segment.startIndex + 1} / ${segment.endIndex + 2}`,
                  }}
                >
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p
                        className={cn(
                          "m-0 truncate rounded-[3px] px-1.5 py-0.5 text-center text-[9px] leading-[13px]",
                          segment.title === null
                            ? "border border-dashed border-foreground/30 text-foreground/65"
                            : "bg-foreground/8 text-foreground/70",
                        )}
                      >
                        {label}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">{label}</TooltipContent>
                  </Tooltip>
                </li>
              );
            })}
          </ul>
        ) : null}
      </TooltipProvider>
    </section>
  );
}
